/**
 * On-air plan indices that must survive {@link VBuffer} mount/unmount, asset-index resyncs, and Vite HMR.
 * Always read/write via {@link getPlaybackPresentationSession()} and {@link getPeakPresentationPlanIdx()}.
 */
export interface PlaybackPresentationSession {
  lastLoggedPlanIdx: number
  lastNotifiedPlanIdx: number
  portalBookAuthorityIdx: number
  /** Last committed on-air frame id (for resume when indices are stale). */
  lastOnAirFrameId: string | null
}

const SESSION_GLOBAL_KEY = '__lucimarkPlaybackPresentationSession'
const PEAK_PLAN_IDX_KEY = '__lucimarkPlaybackPeakPlanIdx'
const ON_AIR_FRAME_ID_KEY = '__lucimarkPlaybackOnAirFrameId'
const INIT_GEN_GLOBAL_KEY = '__lucimarkPlaybackWorkerInitGeneration'
const INIT_BOOTSTRAP_KEY = '__lucimarkInitWorkerBootstrapPending'
const STORAGE_PEAK_KEY = 'lucimark.playbackPeakPlanIdx'
const STORAGE_FRAME_ID_KEY = 'lucimark.playbackOnAirFrameId'
const STORAGE_SPINE_HASH_KEY = 'lucimark.playbackPlanSpineHash'
const RELOAD_PENDING_KEY = 'lucimark.playbackReloadPending'
const RELOAD_LISTENER_KEY = '__lucimarkPlaybackReloadListenerInstalled'
const RELOAD_COLD_START_KEY = '__lucimarkPlaybackReloadColdStartPending'

function globalBag(): Record<string, unknown> {
  return globalThis as Record<string, unknown>
}

function isReloadColdStartPending(): boolean {
  return globalBag()[RELOAD_COLD_START_KEY] === true
}

function setReloadColdStartPending(pending: boolean): void {
  globalBag()[RELOAD_COLD_START_KEY] = pending
}

/** Arm before navigation unloads the document; consumed on next VBuffer mount. */
export function armPlaybackReloadColdStart(): void {
  try {
    if (typeof sessionStorage === 'undefined') {
      return
    }
    sessionStorage.setItem(RELOAD_PENDING_KEY, '1')
  }
  catch {
    // ignore quota / privacy mode
  }
}

function consumePlaybackReloadColdStart(): boolean {
  try {
    if (typeof sessionStorage === 'undefined') {
      return false
    }
    if (sessionStorage.getItem(RELOAD_PENDING_KEY) !== '1') {
      return false
    }
    sessionStorage.removeItem(RELOAD_PENDING_KEY)
    return true
  }
  catch {
    return false
  }
}

/** After initWorker on a reload document, allow sessionStorage restore for HMR within the same session. */
export function clearPlaybackReloadColdStartPending(): void {
  setReloadColdStartPending(false)
}

export function shouldSkipPresentationStorageRestore(): boolean {
  return isReloadColdStartPending()
}

function installPlaybackReloadListenerOnce(): void {
  if (typeof window === 'undefined') {
    return
  }
  const g = globalBag()
  if (g[RELOAD_LISTENER_KEY] === true) {
    return
  }
  g[RELOAD_LISTENER_KEY] = true
  window.addEventListener('pagehide', () => {
    armPlaybackReloadColdStart()
  })
}

installPlaybackReloadListenerOnce()

function readStoredPeakPlanIdx(): number {
  try {
    if (typeof sessionStorage === 'undefined') {
      return -1
    }
    const raw = sessionStorage.getItem(STORAGE_PEAK_KEY)
    if (raw == null) {
      return -1
    }
    const n = Number.parseInt(raw, 10)
    return Number.isFinite(n) ? n : -1
  }
  catch {
    return -1
  }
}

function readStoredOnAirFrameId(): string | null {
  try {
    if (typeof sessionStorage === 'undefined') {
      return null
    }
    const raw = sessionStorage.getItem(STORAGE_FRAME_ID_KEY)
    return raw != null && raw !== '' ? raw : null
  }
  catch {
    return null
  }
}

function readStoredPlanSpineHash(): string {
  try {
    if (typeof sessionStorage === 'undefined') {
      return ''
    }
    return sessionStorage.getItem(STORAGE_SPINE_HASH_KEY) ?? ''
  }
  catch {
    return ''
  }
}

function writeStoredPresentationAuthority(
  planIdx: number,
  frameId?: string | null,
  planSpineHash?: string | null
): void {
  try {
    if (typeof sessionStorage === 'undefined') {
      return
    }
    const prev = readStoredPeakPlanIdx()
    if (planIdx > prev || (planIdx === 0 && prev > 0)) {
      sessionStorage.setItem(STORAGE_PEAK_KEY, String(planIdx))
    }
    if (frameId != null && frameId !== '') {
      sessionStorage.setItem(STORAGE_FRAME_ID_KEY, frameId)
    }
    if (planSpineHash != null && planSpineHash !== '') {
      sessionStorage.setItem(STORAGE_SPINE_HASH_KEY, planSpineHash)
    }
  }
  catch {
    // ignore quota / privacy mode
  }
}

function clearStoredPresentationAuthority(): void {
  try {
    if (typeof sessionStorage === 'undefined') {
      return
    }
    sessionStorage.removeItem(STORAGE_PEAK_KEY)
    sessionStorage.removeItem(STORAGE_FRAME_ID_KEY)
    sessionStorage.removeItem(STORAGE_SPINE_HASH_KEY)
  }
  catch {
    // ignore
  }
}

function inMemoryPeakPlanIdx(): number {
  const g = globalBag()
  return typeof g[PEAK_PLAN_IDX_KEY] === 'number' ? g[PEAK_PLAN_IDX_KEY] as number : -1
}

export function getPlaybackPresentationSession(): PlaybackPresentationSession {
  const g = globalBag()
  if (g[SESSION_GLOBAL_KEY] == null) {
    g[SESSION_GLOBAL_KEY] = {
      lastLoggedPlanIdx: -1,
      lastNotifiedPlanIdx: -1,
      portalBookAuthorityIdx: -1,
      lastOnAirFrameId: null
    } satisfies PlaybackPresentationSession
  }
  const s = g[SESSION_GLOBAL_KEY] as PlaybackPresentationSession
  if (s.lastOnAirFrameId === undefined) {
    s.lastOnAirFrameId = typeof g[ON_AIR_FRAME_ID_KEY] === 'string' ? g[ON_AIR_FRAME_ID_KEY] as string : null
  }
  return s
}

/** True when this document was loaded via browser reload (F5 / refresh). */
export function isPlaybackPageReload(): boolean {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    return nav?.type === 'reload'
  }
  catch {
    return false
  }
}

/**
 * Page reload should always cold-start at frame 1. HMR / soft remount keeps sessionStorage restore.
 */
export function shouldDiscardPresentationStorageOnVBufferMount(): boolean {
  return shouldSkipPresentationStorageRestore()
}

/**
 * VBuffer mount entry: discard stale sessionStorage on page reload, otherwise restore when spine matches.
 */
export function bootstrapPlaybackPresentationOnVBufferMount(currentPlanSpineHash: string): void {
  if (shouldSkipPresentationStorageRestore()) {
    return
  }
  const isReloadColdStart = consumePlaybackReloadColdStart()
    || (isPlaybackPageReload() && peekPlaybackWorkerInitGeneration() === 0)
  if (isReloadColdStart) {
    setReloadColdStartPending(true)
    resetPlaybackPresentationSession()
    return
  }
  restorePlaybackPresentationSessionFromStorage(currentPlanSpineHash)
}

/**
 * Restore in-memory presentation authority from sessionStorage when the spine hash matches.
 * Called on VBuffer remount / HMR — not on page reload (see {@link bootstrapPlaybackPresentationOnVBufferMount}).
 * On spine mismatch or missing storage, clears stale storage and returns without modifying memory.
 */
export function restorePlaybackPresentationSessionFromStorage(currentPlanSpineHash: string): void {
  const storedPeak = readStoredPeakPlanIdx()
  const storedFrameId = readStoredOnAirFrameId()
  const storedSpineHash = readStoredPlanSpineHash()
  if (storedPeak <= 0 && storedFrameId == null) {
    return
  }
  if (currentPlanSpineHash === '') {
    // Queue not materialized yet (common on hard reload). Keep sessionStorage for a later restore.
    return
  }
  if (storedSpineHash === '' || storedSpineHash !== currentPlanSpineHash) {
    clearStoredPresentationAuthority()
    return
  }
  // Spine matches — write directly to avoid any guard logic in recordPresentationPlanIdx.
  const g = globalBag()
  const memPeak = inMemoryPeakPlanIdx()
  const s = getPlaybackPresentationSession()
  const memAuthority = Math.max(memPeak, s.lastLoggedPlanIdx, s.lastNotifiedPlanIdx)
  const restoreIdx = Math.max(storedPeak, memAuthority)
  if (restoreIdx >= 0) {
    if (restoreIdx > memPeak) {
      g[PEAK_PLAN_IDX_KEY] = restoreIdx
    }
    if (restoreIdx > s.lastLoggedPlanIdx) {
      s.lastLoggedPlanIdx = restoreIdx
    }
    if (restoreIdx > s.lastNotifiedPlanIdx) {
      s.lastNotifiedPlanIdx = restoreIdx
    }
  }
  if (storedFrameId != null && storedFrameId !== '' && s.lastOnAirFrameId == null) {
    s.lastOnAirFrameId = storedFrameId
    g[ON_AIR_FRAME_ID_KEY] = storedFrameId
  }
}

export function getPeakPresentationPlanIdx(): number {
  const peak = inMemoryPeakPlanIdx()
  const s = getPlaybackPresentationSession()
  return Math.max(peak, s.lastLoggedPlanIdx, s.lastNotifiedPlanIdx)
}

/**
 * Monotonic presentation peak can outlive a shorter rematched plan (playlist cutover /
 * aspect filter). Using the stale peak in wrap guards treats a legitimate last→head
 * advance as mid-loop head reset / clamps it back (#1662 Slimbox).
 *
 * When `authorityIdx >= planLen`, prefer `fallbackIdx` (on-air / visible plan idx), else
 * the last plan slot.
 */
export function healStalePresentationAuthorityIdx(
  authorityIdx: number,
  planLen: number,
  fallbackIdx: number = -1
): number {
  if (planLen <= 0 || authorityIdx < 0) {
    return authorityIdx
  }
  if (authorityIdx < planLen) {
    return authorityIdx
  }
  if (fallbackIdx >= 0 && fallbackIdx < planLen) {
    return fallbackIdx
  }
  return planLen - 1
}

export function recordPresentationPlanIdx(
  planIdx: number,
  frameId?: string | null,
  planSpineHash?: string | null
): void {
  if (planIdx < 0) {
    return
  }
  const g = globalBag()
  const prevPeak = inMemoryPeakPlanIdx()
  const s = getPlaybackPresentationSession()
  const authority = Math.max(prevPeak, s.lastLoggedPlanIdx, s.lastNotifiedPlanIdx)
  if (planIdx === 0 && authority >= 1) {
    // Legitimate loop wrap (last slot → head). Reset monotonic authority so
    // clampSequentialPlanIdx accepts 0→1→2 instead of clamping back to 0.
    g[PEAK_PLAN_IDX_KEY] = 0
    s.lastLoggedPlanIdx = 0
    s.lastNotifiedPlanIdx = 0
    if (frameId != null && frameId !== '') {
      s.lastOnAirFrameId = frameId
      g[ON_AIR_FRAME_ID_KEY] = frameId
    }
    writeStoredPresentationAuthority(0, frameId ?? s.lastOnAirFrameId, planSpineHash)
    return
  }
  if (planIdx > prevPeak) {
    g[PEAK_PLAN_IDX_KEY] = planIdx
  }
  if (planIdx > s.lastLoggedPlanIdx) {
    s.lastLoggedPlanIdx = planIdx
  }
  if (planIdx > s.lastNotifiedPlanIdx) {
    s.lastNotifiedPlanIdx = planIdx
  }
  if (frameId != null && frameId !== '') {
    s.lastOnAirFrameId = frameId
    g[ON_AIR_FRAME_ID_KEY] = frameId
  }
  writeStoredPresentationAuthority(planIdx, frameId ?? s.lastOnAirFrameId, planSpineHash)
}

/**
 * Force presentation authority to an exact plan index (schedule rematch / playlist swap).
 * Unlike {@link recordPresentationPlanIdx}, this may lower the monotonic peak — required when
 * the worker rematches mid-loop and VBuffer would otherwise keep a higher stale peak
 * (`scheduler_main_desync`: presentationNotifiedIdx ≫ workerNotifiedIdx).
 */
export function forceSetPresentationPlanIdx(
  planIdx: number,
  frameId?: string | null,
  planSpineHash?: string | null
): void {
  if (planIdx < 0) {
    return
  }
  const g = globalBag()
  const s = getPlaybackPresentationSession()
  g[PEAK_PLAN_IDX_KEY] = planIdx
  s.lastLoggedPlanIdx = planIdx
  s.lastNotifiedPlanIdx = planIdx
  /** Drop stale book authority so max() helpers cannot resurrect a pre-swap index. */
  s.portalBookAuthorityIdx = planIdx
  if (frameId != null && frameId !== '') {
    s.lastOnAirFrameId = frameId
    g[ON_AIR_FRAME_ID_KEY] = frameId
  }
  writeStoredPresentationAuthority(planIdx, frameId ?? s.lastOnAirFrameId, planSpineHash)
}

export function resetPlaybackPresentationSession(): void {
  const g = globalBag()
  g[SESSION_GLOBAL_KEY] = {
    lastLoggedPlanIdx: -1,
    lastNotifiedPlanIdx: -1,
    portalBookAuthorityIdx: -1,
    lastOnAirFrameId: null
  } satisfies PlaybackPresentationSession
  g[PEAK_PLAN_IDX_KEY] = -1
  g[ON_AIR_FRAME_ID_KEY] = null
  clearStoredPresentationAuthority()
}

export function peekPlaybackWorkerInitGeneration(): number {
  const g = globalBag()
  return typeof g[INIT_GEN_GLOBAL_KEY] === 'number' ? g[INIT_GEN_GLOBAL_KEY] as number : 0
}

export function nextPlaybackWorkerInitGeneration(): number {
  const g = globalBag()
  const prev = peekPlaybackWorkerInitGeneration()
  const next = prev + 1
  g[INIT_GEN_GLOBAL_KEY] = next
  return next
}

export function isInitWorkerBootstrapPending(): boolean {
  return globalBag()[INIT_BOOTSTRAP_KEY] !== false
}

export function setInitWorkerBootstrapPending(pending: boolean): void {
  globalBag()[INIT_BOOTSTRAP_KEY] = pending
}

export function playbackPresentationAuthorityFromSession(): number {
  const s = getPlaybackPresentationSession()
  return Math.max(
    inMemoryPeakPlanIdx(),
    s.lastLoggedPlanIdx,
    s.lastNotifiedPlanIdx,
    s.portalBookAuthorityIdx >= 0 ? s.portalBookAuthorityIdx : -1
  )
}

/**
 * E2E only: clear in-memory presentation authority while leaving sessionStorage intact
 * (simulates Vite HMR module re-eval before {@link VBuffer} remount).
 */
export function simulatePlaybackPresentationHmrRemountForE2e(): void {
  const g = globalBag()
  delete g[SESSION_GLOBAL_KEY]
  g[PEAK_PLAN_IDX_KEY] = -1
  g[ON_AIR_FRAME_ID_KEY] = null
}

/**
 * E2E only: leave monotonic peak / logged / notified ≥ current planLen (#1662).
 * Models aspect-filter / rematch leave-behind after a longer plan was recorded.
 */
export function poisonStalePresentationPeakForE2e(peakIdx: number): void {
  if (peakIdx < 0) {
    return
  }
  const g = globalBag()
  const s = getPlaybackPresentationSession()
  g[PEAK_PLAN_IDX_KEY] = peakIdx
  s.lastLoggedPlanIdx = peakIdx
  s.lastNotifiedPlanIdx = peakIdx
  s.portalBookAuthorityIdx = peakIdx
  writeStoredPresentationAuthority(peakIdx, s.lastOnAirFrameId, null)
}
